import { useEffect, useState } from 'react'
import type { FormEvent } from 'react'

import { api } from '../api/client'
import type { CondizioneRegola, OggettoTaggabile, RegolaTag, RegolaTagIn, TagCatalogo, TagCoverage, TagPolicy } from '../api/types'
import { usePermessiModuli } from '../auth/permessi'
import { Card, EmptyState, ErrorBanner, formatDateTime, PageSection, SectionedPage, Spinner } from '../components/common'
import { DataTable } from '../components/DataTable'
import { TagChip } from '../components/Tag'
import { useAsync } from '../hooks/useAsync'
import { t } from '../i18n/lingua'

/**
 * I tag della piattaforma: il catalogo (chiave:valore, colore, dove sono usati)
 * e le regole che li mettono da sole.
 *
 * I tag si mettono sugli oggetti dalla loro scheda o dalla colonna «Tag» degli
 * elenchi (CI, asset, business service, contratti, credenziali, pool, piani,
 * sonde); qui si governano. Una regola («i CI nella 10.1.0.0/16 → sede:Milano»)
 * vale subito sugli oggetti che ci sono e poi su ogni CI a ogni raccolta; se un
 * oggetto smette di soddisfarla, perde il tag che la regola gli aveva messo.
 */

function nomiOggetti(): Record<OggettoTaggabile, string> {
  return {
    ci: t('CI'),
    asset: t('Asset'),
    business_service: t('Servizi'),
    contract: t('Contratti'),
    credential: t('Credenziali'),
    pool: t('Pool'),
    discovery_plan: t('Piani'),
    appliance: t('Sonde'),
    inventory_agent: t('Agenti'),
  }
}

const COLORI = ['#6b7280', '#c8102e', '#e8590c', '#f59f00', '#2f9e44', '#0c8599', '#1971c2', '#6741d9', '#c2255c']

export default function Tags() {
  const catalogo = useAsync(() => api.tags(), [])
  const permessi = usePermessiModuli()
  const scrive = permessi.scrive('tags')

  return (
    <>
      <header className="page__header">
        <div>
          <h1 className="page__title">{t('Tag')}</h1>
          <p className="page__subtitle">
            {t('Classificazione di CI, asset, servizi, contratti, credenziali, pool, piani di discovery e sonde tramite attributi strutturati (chiave:valore), assegnati manualmente o automaticamente mediante regole. Consentono di organizzare, filtrare e correlare gli oggetti in base a caratteristiche comuni.')}
          </p>
        </div>
      </header>
      <SectionedPage
        sections={[
          { id: 'catalogo', label: t('Catalogo') },
          { id: 'regole', label: t('Regole') },
          { id: 'obbligatori', label: t('Obbligatori') },
        ]}
      >
        <PageSection id="catalogo">
          <Catalogo stato={catalogo} scrive={scrive} />
        </PageSection>
        <PageSection id="regole">
          <Regole scrive={scrive} onChanged={() => void catalogo.reload()} />
        </PageSection>
        <PageSection id="obbligatori">
          <TagObbligatori scrive={scrive} />
        </PageSection>
      </SectionedPage>
    </>
  )
}

function Catalogo({ stato, scrive }: { stato: ReturnType<typeof useAsync<TagCatalogo[]>>; scrive: boolean }) {
  const [errore, setErrore] = useState<unknown>(null)
  const [chiave, setChiave] = useState('')
  const [valore, setValore] = useState('')
  const [colore, setColore] = useState(COLORI[6])
  const [modifica, setModifica] = useState<string | null>(null)
  const oggetti = nomiOggetti()

  async function fai(fare: () => Promise<unknown>) {
    setErrore(null)
    try {
      await fare()
      await stato.reload()
    } catch (err) {
      setErrore(err)
    }
  }

  async function crea(e: FormEvent) {
    e.preventDefault()
    await fai(() => api.createTag({ key: chiave, value: valore, color: colore }))
    setChiave('')
    setValore('')
  }

  return (
    <Card title={t('Catalogo dei tag')}>
      <ErrorBanner error={errore ?? stato.error} />
      {scrive && (
        <form className="tag-nuovo" onSubmit={crea}>
          <input value={chiave} onChange={(e) => setChiave(e.target.value)} placeholder={t('chiave (es. ambiente)')} required />
          <span aria-hidden="true">:</span>
          <input value={valore} onChange={(e) => setValore(e.target.value)} placeholder={t('valore (facoltativo)')} />
          <Colori valore={colore} onChange={setColore} />
          <button type="submit">{t('Crea tag')}</button>
        </form>
      )}
      {stato.loading && !stato.data ? (
        <Spinner />
      ) : !stato.data?.length ? (
        <EmptyState
          title={t('Nessun tag')}
          hint={t('Crea un tag qui, oppure scrivilo direttamente su un oggetto («ambiente:produzione») o con una regola.')}
        />
      ) : (
        <DataTable
          rows={stato.data}
          rowKey={(x) => x.id}
          initialSort={{ key: 'label' }}
          paginazione={{ predefinita: 25 }}
          conteggio={{ nome: t('tag') }}
          expanded={(x) => modifica === x.id}
          expand={(x) => (
            <ModificaTag
              tag={x}
              onFatto={() => {
                setModifica(null)
                void stato.reload()
              }}
            />
          )}
          columns={[
            { key: 'label', header: t('Tag'), value: (x) => x.label, render: (x) => <TagChip tag={x} /> },
            { key: 'description', header: t('Descrizione'), value: (x) => x.description },
            {
              key: 'uso',
              header: t('Dove è usato'),
              value: (x) => x.total,
              render: (x) =>
                x.total === 0 ? (
                  <span className="muted">{t('mai')}</span>
                ) : (
                  <span className="tag-uso">
                    {(Object.entries(x.counts) as [OggettoTaggabile, number][]).map(([k, n]) => (
                      <span key={k} className="chip">
                        {oggetti[k]} {n}
                      </span>
                    ))}
                  </span>
                ),
            },
            ...(scrive
              ? [
                  {
                    key: 'azioni',
                    header: '',
                    filter: false,
                    sort: false,
                    value: () => '',
                    render: (x: TagCatalogo) => (
                      <span style={{ whiteSpace: 'nowrap' }}>
                        <button className="link" onClick={() => setModifica(modifica === x.id ? null : x.id)}>
                          {modifica === x.id ? t('Chiudi') : t('Modifica')}
                        </button>{' '}
                        <button
                          className="link"
                          onClick={() => {
                            if (window.confirm(t('Eliminare il tag «{tag}»? Viene tolto da {n} oggetti.', { tag: x.label, n: x.total })))
                              void fai(() => api.deleteTag(x.id))
                          }}
                        >
                          {t('Elimina')}
                        </button>
                      </span>
                    ),
                  },
                ]
              : []),
          ]}
        />
      )}
    </Card>
  )
}

function Colori({ valore, onChange }: { valore: string; onChange: (c: string) => void }) {
  return (
    <span className="tag-colori" role="radiogroup" aria-label={t('Colore')}>
      {COLORI.map((c) => (
        <button
          type="button"
          key={c}
          role="radio"
          aria-checked={valore === c}
          aria-label={c}
          className={`ruoli__colore${valore === c ? ' ruoli__colore--scelto' : ''}`}
          style={{ background: c }}
          onClick={() => onChange(c)}
        />
      ))}
    </span>
  )
}

function ModificaTag({ tag, onFatto }: { tag: TagCatalogo; onFatto: () => void }) {
  const [chiave, setChiave] = useState(tag.key)
  const [valore, setValore] = useState(tag.value)
  const [colore, setColore] = useState(tag.color)
  const [descrizione, setDescrizione] = useState(tag.description)
  const [errore, setErrore] = useState<unknown>(null)

  async function salva(e: FormEvent) {
    e.preventDefault()
    setErrore(null)
    try {
      await api.updateTag(tag.id, { key: chiave, value: valore, color: colore, description: descrizione })
      onFatto()
    } catch (err) {
      setErrore(err)
    }
  }

  return (
    <form className="tag-modifica" onSubmit={salva}>
      <ErrorBanner error={errore} />
      <div className="row">
        <label className="field">
          <span>{t('Chiave')}</span>
          <input value={chiave} onChange={(e) => setChiave(e.target.value)} required />
        </label>
        <label className="field">
          <span>{t('Valore')}</span>
          <input value={valore} onChange={(e) => setValore(e.target.value)} />
        </label>
        <label className="field">
          <span>{t('Descrizione')}</span>
          <input value={descrizione} onChange={(e) => setDescrizione(e.target.value)} maxLength={300} />
        </label>
      </div>
      <Colori valore={colore} onChange={setColore} />
      <p className="field__hint">{t('Rinominare o ricolorare un tag vale ovunque è assegnato.')}</p>
      <button type="submit">{t('Salva modifiche')}</button>
    </form>
  )
}

// --- Regole ---

function campiDi(tipo: 'ci' | 'asset'): { value: string; label: string }[] {
  return tipo === 'ci'
    ? [
        { value: 'name', label: t('Nome') },
        { value: 'ci_class', label: t('Classe') },
        { value: 'ci_type', label: t('Tipo') },
        { value: 'vendor', label: t('Produttore') },
        { value: 'model', label: t('Modello') },
        { value: 'os', label: t('Sistema operativo') },
        { value: 'source', label: t('Origine') },
        { value: 'mgmt_ip', label: t('Indirizzo di gestione') },
        { value: 'ip', label: t('Qualunque indirizzo IP') },
        { value: 'attr:', label: t('Attributo…') },
      ]
    : [
        { value: 'name', label: t('Nome') },
        { value: 'asset_tag', label: t('Cartellino') },
        { value: 'category', label: t('Categoria') },
        { value: 'status', label: t('Stato') },
        { value: 'vendor', label: t('Produttore') },
        { value: 'model', label: t('Modello') },
        { value: 'location', label: t('Ubicazione') },
        { value: 'owner', label: t('Assegnatario') },
        { value: 'cost_center', label: t('Centro di costo') },
        { value: 'serial', label: t('Numero di serie') },
      ]
}

function operatori(): { value: CondizioneRegola['op']; label: string }[] {
  return [
    { value: 'equals', label: t('è uguale a') },
    { value: 'not_equals', label: t('è diverso da') },
    { value: 'contains', label: t('contiene') },
    { value: 'starts', label: t('inizia con') },
    { value: 'regex', label: t('espressione regolare') },
    { value: 'in_subnet', label: t('è nella subnet') },
    { value: 'exists', label: t('è valorizzato') },
  ]
}

function vuota(): RegolaTagIn {
  return {
    name: '',
    enabled: true,
    object_type: 'ci',
    conditions: [{ field: 'ip', op: 'in_subnet', value: '' }],
    tag_key: '',
    tag_value: '',
    color: COLORI[6],
  }
}

function Regole({ scrive, onChanged }: { scrive: boolean; onChanged: () => void }) {
  const regole = useAsync(() => api.tagRules(), [])
  const [bozza, setBozza] = useState<RegolaTagIn | null>(null)
  const [id, setId] = useState<string | null>(null)
  const [errore, setErrore] = useState<unknown>(null)
  const [esito, setEsito] = useState('')

  async function fai(fare: () => Promise<{ result?: { added: number; removed: number; matched: number } } | void>) {
    setErrore(null)
    setEsito('')
    try {
      const r = await fare()
      if (r && 'result' in r && r.result) {
        setEsito(
          t('Applicata: {matched} oggetti con il tag, {added} aggiunti, {removed} tolti.', {
            matched: r.result.matched,
            added: r.result.added,
            removed: r.result.removed,
          }),
        )
      }
      await regole.reload()
      onChanged()
    } catch (err) {
      setErrore(err)
    }
  }

  return (
    <Card
      title={t('Regole')}
      actions={
        scrive && !bozza ? (
          <button onClick={() => { setBozza(vuota()); setId(null) }}>+ {t('Nuova regola')}</button>
        ) : undefined
      }
    >
      <p className="field__hint" style={{ marginTop: 0 }}>
        {t('Una regola mette il tag sugli oggetti che soddisfano tutte le condizioni: subito, e sui CI a ogni raccolta. Chi smette di soddisfarla perde il tag della regola; i tag messi a mano restano.')}
      </p>
      <ErrorBanner error={errore ?? regole.error} />
      {esito && <div className="banner banner--ok">{esito}</div>}
      {bozza && (
        <EditorRegola
          bozza={bozza}
          onChange={setBozza}
          onAnnulla={() => setBozza(null)}
          onSalva={async () => {
            await fai(() => (id ? api.updateTagRule(id, bozza) : api.createTagRule(bozza)))
            setBozza(null)
            setId(null)
          }}
        />
      )}
      {regole.loading && !regole.data ? (
        <Spinner />
      ) : !regole.data?.length ? (
        <EmptyState
          title={t('Nessuna regola')}
          hint={t('Per esempio: i CI nella subnet 10.1.0.0/16 → sede:Milano; classe firewall → criticità:alta; il tag cloud Environment → ambiente:{attr:tag_Environment}.')}
        />
      ) : (
        <DataTable
          rows={regole.data}
          rowKey={(r) => r.id}
          initialSort={{ key: 'name' }}
          columns={[
            { key: 'name', header: t('Nome'), value: (r) => r.name },
            {
              key: 'tag',
              header: t('Tag'),
              value: (r) => `${r.tag_key}:${r.tag_value}`,
              render: (r) => <TagChip tag={{ key: r.tag_key, value: r.tag_value, color: r.color }} />,
            },
            {
              key: 'cond',
              header: t('Condizioni'),
              value: (r) => r.conditions.map((c) => `${c.field} ${c.op} ${c.value}`).join(' · '),
              render: (r) => (
                <span className="regola-condizioni">
                  <span className="chip">{nomiOggetti()[r.object_type]}</span>
                  {r.conditions.map((c, i) => (
                    <span key={i} className="mono">
                      {c.field} {operatori().find((o) => o.value === c.op)?.label ?? c.op} {c.value}
                    </span>
                  ))}
                </span>
              ),
            },
            {
              key: 'stato',
              header: t('Oggetti'),
              value: (r) => r.last_matched,
              render: (r) => (
                <span title={r.last_run_at ? t('Ultima applicazione: {quando}', { quando: formatDateTime(r.last_run_at) }) : undefined}>
                  {r.enabled ? r.last_matched : <span className="muted">{t('spenta')}</span>}
                </span>
              ),
            },
            ...(scrive
              ? [
                  {
                    key: 'azioni',
                    header: '',
                    filter: false,
                    sort: false,
                    value: () => '',
                    render: (r: RegolaTag) => (
                      <span style={{ whiteSpace: 'nowrap' }}>
                        <button className="link" onClick={() => { setId(r.id); setBozza({ ...r }) }}>
                          {t('Modifica')}
                        </button>{' '}
                        <button className="link" onClick={() => void fai(() => api.applyTagRule(r.id))}>
                          {t('Applica ora')}
                        </button>{' '}
                        <button
                          className="link"
                          onClick={() => {
                            if (window.confirm(t('Eliminare la regola «{nome}»? I tag che aveva messo vengono tolti.', { nome: r.name })))
                              void fai(() => api.deleteTagRule(r.id))
                          }}
                        >
                          {t('Elimina')}
                        </button>
                      </span>
                    ),
                  },
                ]
              : []),
          ]}
        />
      )}
    </Card>
  )
}

function EditorRegola({
  bozza,
  onChange,
  onSalva,
  onAnnulla,
}: {
  bozza: RegolaTagIn
  onChange: (b: RegolaTagIn) => void
  onSalva: () => Promise<void>
  onAnnulla: () => void
}) {
  const [anteprima, setAnteprima] = useState<{ matched: number; examples: { id: string; name: string; tag: string }[] } | null>(null)
  const [errAnteprima, setErrAnteprima] = useState<unknown>(null)
  const [lavoro, setLavoro] = useState(false)
  const campi = campiDi(bozza.object_type)

  // L'anteprima si aggiorna da sola mentre si scrive (con una pausa): è la
  // risposta a «quanti ne prende?» prima di salvare.
  useEffect(() => {
    const pronta = bozza.tag_key.trim() && bozza.conditions.every((c) => c.op === 'exists' || c.value.trim())
    if (!pronta) {
      setAnteprima(null)
      return
    }
    const timer = window.setTimeout(() => {
      api
        .previewTagRule(bozza)
        .then((r) => {
          setAnteprima(r)
          setErrAnteprima(null)
        })
        .catch((err) => {
          setAnteprima(null)
          setErrAnteprima(err)
        })
    }, 500)
    return () => window.clearTimeout(timer)
  }, [bozza])

  const cambiaCondizione = (i: number, c: Partial<CondizioneRegola>) =>
    onChange({ ...bozza, conditions: bozza.conditions.map((x, k) => (k === i ? { ...x, ...c } : x)) })

  return (
    <form
      className="regola-editor"
      onSubmit={async (e) => {
        e.preventDefault()
        setLavoro(true)
        try {
          await onSalva()
        } finally {
          setLavoro(false)
        }
      }}
    >
      <div className="row">
        <label className="field">
          <span>{t('Nome della regola')}</span>
          <input value={bozza.name} onChange={(e) => onChange({ ...bozza, name: e.target.value })} required
                 placeholder={t('Sede di Milano')} />
        </label>
        <label className="field" style={{ maxWidth: 200 }}>
          <span>{t('Si applica a')}</span>
          <select
            value={bozza.object_type}
            onChange={(e) =>
              onChange({
                ...bozza,
                object_type: e.target.value as 'ci' | 'asset',
                conditions: [{ field: 'name', op: 'contains', value: '' }],
              })
            }
          >
            <option value="ci">{t('Configuration Item')}</option>
            <option value="asset">{t('Asset')}</option>
          </select>
        </label>
        <label className="checkbox" style={{ alignSelf: 'end' }}>
          <input type="checkbox" checked={bozza.enabled} onChange={(e) => onChange({ ...bozza, enabled: e.target.checked })} />
          <span>{t('Attiva')}</span>
        </label>
      </div>

      <h4>{t('Quando (tutte le condizioni)')}</h4>
      {bozza.conditions.map((c, i) => {
        const attributo = c.field.startsWith('attr:')
        return (
          <div className="regola-condizione" key={i}>
            <select
              value={attributo ? 'attr:' : c.field}
              onChange={(e) => cambiaCondizione(i, { field: e.target.value })}
            >
              {campi.map((f) => (
                <option key={f.value} value={f.value}>
                  {f.label}
                </option>
              ))}
            </select>
            {attributo && (
              <input
                className="mono"
                value={c.field.slice(5)}
                onChange={(e) => cambiaCondizione(i, { field: `attr:${e.target.value}` })}
                placeholder={t('nome attributo')}
                required
              />
            )}
            <select value={c.op} onChange={(e) => cambiaCondizione(i, { op: e.target.value as CondizioneRegola['op'] })}>
              {operatori().map((o) => (
                <option key={o.value} value={o.value}>
                  {o.label}
                </option>
              ))}
            </select>
            {c.op !== 'exists' && (
              <input
                value={c.value}
                onChange={(e) => cambiaCondizione(i, { value: e.target.value })}
                placeholder={c.op === 'in_subnet' ? '10.1.0.0/16' : c.op === 'regex' ? '^srv-' : ''}
                required
              />
            )}
            {bozza.conditions.length > 1 && (
              <button
                type="button"
                className="link"
                onClick={() => onChange({ ...bozza, conditions: bozza.conditions.filter((_, k) => k !== i) })}
                aria-label={t('Togli la condizione')}
              >
                ×
              </button>
            )}
          </div>
        )
      })}
      {bozza.conditions.length < 20 && (
        <button
          type="button"
          className="link"
          onClick={() => onChange({ ...bozza, conditions: [...bozza.conditions, { field: 'name', op: 'contains', value: '' }] })}
        >
          + {t('Aggiungi condizione')}
        </button>
      )}

      <h4>{t('Allora metti il tag')}</h4>
      <div className="regola-tag">
        <input value={bozza.tag_key} onChange={(e) => onChange({ ...bozza, tag_key: e.target.value })}
               placeholder={t('chiave')} required />
        <span aria-hidden="true">:</span>
        <input value={bozza.tag_value} onChange={(e) => onChange({ ...bozza, tag_value: e.target.value })}
               placeholder={t('valore (facoltativo)')} />
        <Colori valore={bozza.color} onChange={(c) => onChange({ ...bozza, color: c })} />
      </div>
      <p className="field__hint">
        {t('Il valore può prendere un dato dell’oggetto: {vendor}, {ci_class}, {attr:tag_Environment} (i tag cloud). Se il dato manca, il tag non si mette.')}
      </p>

      <div className="regola-anteprima" aria-live="polite">
        <ErrorBanner error={errAnteprima} />
        {anteprima && (
          <>
            <strong>{t('{n} oggetti riceverebbero il tag', { n: anteprima.matched })}</strong>
            {anteprima.examples.length > 0 && (
              <span className="muted">
                {' — '}
                {anteprima.examples.map((x) => `${x.name} (${x.tag})`).join(', ')}
                {anteprima.matched > anteprima.examples.length ? '…' : ''}
              </span>
            )}
          </>
        )}
      </div>

      <button type="submit" disabled={lavoro}>
        {lavoro ? t('Salvataggio…') : t('Salva e applica')}
      </button>{' '}
      <button type="button" className="secondary" onClick={onAnnulla}>
        {t('Annulla')}
      </button>
    </form>
  )
}

/**
 * Tag obbligatori: chiavi che ogni CI deve portare (tipicamente «ambiente»), con
 * un vocabolario di valori ammessi. Si sorveglia la copertura — quanti CI ce
 * l'hanno, quanti no — e si assegnano in blocco i mancanti. L'obbligo non blocca
 * la scoperta: certe operazioni (il patching) lo pretendono.
 */
function TagObbligatori({ scrive }: { scrive: boolean }) {
  const policy = useAsync(() => api.tagPolicy(), [])
  const copertura = useAsync(() => api.tagCoverage('ci'), [])
  const [bozza, setBozza] = useState<TagPolicy | null>(null)
  const [errore, setErrore] = useState<string | null>(null)
  const [nuovaChiave, setNuovaChiave] = useState('')

  const p = bozza ?? policy.data ?? null

  function aggiorna(next: TagPolicy) {
    setBozza(next)
  }

  async function salva() {
    if (!p) return
    setErrore(null)
    try {
      await api.setTagPolicy(p)
      setBozza(null)
      await Promise.all([policy.reload(), copertura.reload()])
    } catch (e) {
      setErrore(e instanceof Error ? e.message : String(e))
    }
  }

  function preset() {
    aggiorna({
      required_keys: Array.from(new Set([...(p?.required_keys ?? []), 'ambiente'])),
      allowed_values: {
        ...(p?.allowed_values ?? {}),
        ambiente: ['produzione', 'collaudo', 'sviluppo', 'test'],
      },
    })
  }

  if (policy.loading && !policy.data) return <Spinner />

  return (
    <div>
      {errore && <ErrorBanner error={errore} />}
      <Card title={t('Chiavi obbligatorie e valori ammessi')}>
        <p className="field__hint">
          {t('Le chiavi qui elencate devono stare su ogni CI; se una chiave ha dei valori ammessi, solo quelli si possono usare. Esempio tipico: «ambiente» con produzione/collaudo/sviluppo. L’obbligo si sorveglia (copertura qui sotto) e il patching lo pretende, ma non blocca la scoperta.')}
        </p>
        {scrive && (!p || p.required_keys.length === 0) && (
          <button className="pulsante-link" onClick={preset}>{t('Attiva «ambiente» standard')}</button>
        )}
        {p && p.required_keys.map((chiave) => (
          <div key={chiave} className="field" style={{ borderTop: '1px solid var(--bordo,#eee)', paddingTop: 8 }}>
            <label>{chiave}{' '}
              {scrive && (
                <button className="link" onClick={() => aggiorna({
                  required_keys: p.required_keys.filter((k) => k !== chiave),
                  allowed_values: Object.fromEntries(Object.entries(p.allowed_values).filter(([k]) => k !== chiave)),
                })}>{t('togli')}</button>
              )}
            </label>
            <div className="rowactions" style={{ flexWrap: 'wrap', gap: 4 }}>
              {(p.allowed_values[chiave] ?? []).map((v) => (
                <span key={v} className="chip">
                  {v}{scrive && (
                    <button className="link" onClick={() => aggiorna({
                      ...p,
                      allowed_values: { ...p.allowed_values, [chiave]: p.allowed_values[chiave].filter((x) => x !== v) },
                    })}> ×</button>
                  )}
                </span>
              ))}
              {(p.allowed_values[chiave] ?? []).length === 0 && (
                <span className="field__hint">{t('qualunque valore (nessun vocabolario)')}</span>
              )}
            </div>
            {scrive && (
              <input
                type="text"
                placeholder={t('aggiungi un valore ammesso e premi Invio')}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    const v = (e.target as HTMLInputElement).value.trim()
                    if (v) {
                      aggiorna({ ...p, allowed_values: {
                        ...p.allowed_values,
                        [chiave]: Array.from(new Set([...(p.allowed_values[chiave] ?? []), v])),
                      } })
                      ;(e.target as HTMLInputElement).value = ''
                    }
                  }
                }}
              />
            )}
          </div>
        ))}
        {scrive && (
          <div className="field" style={{ marginTop: 8 }}>
            <label>{t('Aggiungi una chiave obbligatoria')}</label>
            <div className="rowactions">
              <input type="text" value={nuovaChiave} onChange={(e) => setNuovaChiave(e.target.value)} />
              <button className="pulsante-link" disabled={!nuovaChiave.trim()} onClick={() => {
                const k = nuovaChiave.trim().toLowerCase()
                aggiorna({
                  required_keys: Array.from(new Set([...(p?.required_keys ?? []), k])),
                  allowed_values: p?.allowed_values ?? {},
                })
                setNuovaChiave('')
              }}>{t('Aggiungi')}</button>
            </div>
          </div>
        )}
        {scrive && bozza && (
          <div className="rowactions" style={{ marginTop: 8 }}>
            <button className="pulsante-link" onClick={salva}>{t('Salva')}</button>
            <button className="link" onClick={() => setBozza(null)}>{t('Annulla')}</button>
          </div>
        )}
      </Card>

      <Card title={t('Copertura sui CI')}>
        {copertura.loading && <Spinner />}
        {copertura.data && copertura.data.length === 0 && (
          <EmptyState title={t('Nessuna chiave obbligatoria definita.')} />
        )}
        {copertura.data?.map((c: TagCoverage) => (
          <CoperturaChiave key={c.key} c={c} scrive={scrive} onChanged={() => void copertura.reload()} setErrore={setErrore} />
        ))}
      </Card>
    </div>
  )
}

function CoperturaChiave({ c, scrive, onChanged, setErrore }: {
  c: TagCoverage; scrive: boolean; onChanged: () => void; setErrore: (s: string | null) => void
}) {
  const [valore, setValore] = useState(c.allowed_values[0] ?? '')
  const pct = c.total ? Math.round((c.with / c.total) * 100) : 100

  async function assegnaMancanti() {
    setErrore(null)
    try {
      const { ids } = await api.tagCoverageMissing(c.key, 'ci')
      if (ids.length === 0) return
      await api.assignTags('ci', ids, { tags: [`${c.key}:${valore}`] })
      onChanged()
    } catch (e) {
      setErrore(e instanceof Error ? e.message : String(e))
    }
  }

  return (
    <div className="field" style={{ borderTop: '1px solid var(--bordo,#eee)', paddingTop: 8 }}>
      <label>{c.key}</label>
      <div style={{ background: 'var(--superficie-2,#eee)', borderRadius: 4, height: 8, overflow: 'hidden' }}>
        <div style={{ width: `${pct}%`, height: '100%', background: c.missing ? 'var(--giallo,#f59f00)' : 'var(--verde,#2f9e44)' }} />
      </div>
      <p className="field__hint">
        {t('{con} di {tot} CI hanno «{chiave}»; {mancano} mancanti.', { con: c.with, tot: c.total, chiave: c.key, mancano: c.missing })}
      </p>
      {scrive && c.missing > 0 && (
        <div className="rowactions">
          {c.allowed_values.length > 0 ? (
            <select value={valore} onChange={(e) => setValore(e.target.value)}>
              {c.allowed_values.map((v) => <option key={v} value={v}>{v}</option>)}
            </select>
          ) : (
            <input type="text" value={valore} onChange={(e) => setValore(e.target.value)} placeholder={t('valore')} />
          )}
          <button className="pulsante-link" disabled={!valore.trim()} onClick={assegnaMancanti}>
            {t('Assegna «{val}» ai {n} CI senza', { val: valore, n: c.missing })}
          </button>
        </div>
      )}
    </div>
  )
}
